import type { ProductFormulation } from '@/constants/products';
import BigNumber from 'bignumber.js';

import { roundUpDispenseAmount } from './roundUpDispenseAmount';

/** A dispense limit the prescriber has set on a formulation; absent, the limit is the amount prescribed */
type FormulationDispenseLimit = {
  /** As parsed from the input, at the precision dispense amounts are shown; NaN when it could not be read as a number */
  limit: number;
  /** The prescriber has acknowledged that the limit is below the amount prescribed */
  isConfirmed: boolean;
};

/** Limits the prescriber set, by formulation; a formulation with none tracks the amount prescribed */
export type DispenseLimitsByFormulation = Partial<Record<ProductFormulation, FormulationDispenseLimit>>;

/** Where the limit in force stands against the amount prescribed */
export type DispenseLimitStatus = 'under' | 'over' | 'match';

/** The amount prescribed and the limit in force, named so the two can't be swapped */
type DispenseLimitComparison = { prescribed: number; limit: number };

/** A limit the prescriber typed can be applied when it reads as a number greater than 0. */
export const isValidDispenseLimit = (limit: number) => Number.isFinite(limit) && limit > 0;

/**
 * A typed limit rounded up to the precision dispense amounts are shown and written at. An unreadable limit passes
 * through, so the row can still flag it.
 */
export const roundDispenseLimit = (limit: number) =>
  Number.isFinite(limit) ? roundUpDispenseAmount(limit).toNumber() : limit;

export const getDispenseLimitStatus = ({ prescribed, limit }: DispenseLimitComparison): DispenseLimitStatus => {
  if (limit > prescribed) {
    return 'under';
  }

  if (limit < prescribed) {
    return 'over';
  }

  return 'match';
};

/** Limit − prescribed, exact: positive is headroom left under the limit, negative the amount over it. */
export const getDispenseLimitHeadroom = ({ prescribed, limit }: DispenseLimitComparison) =>
  BigNumber(limit).minus(prescribed).toNumber();
